import { useParams } from "react-router-dom";

import {
  useStaffDetails,
  useStaffUser,
} from "../../../hooks/queries/admin/staff";
import {
  AdminBreadcrumb,
  ErrorMessage,
  PageContainer,
  PageHeader,
  PageIntro,
  PageTitle,
  StaffDetailsPanel,
} from "../shared";

/**
 * Staff details route (EN-5150) — the page shell around the shared
 * `StaffDetailsPanel`. Retool opened the module in a drawer over the list; a
 * route makes a staff member linkable and, more importantly, keeps the panel
 * out of the global modal slot, which its embedded audit trails need for
 * their row drill-down.
 *
 * The breadcrumb replaces a Back button: it names where you'd return to *and*
 * where you are, which a Back button can't. The staff/user queries here are
 * the same cache entries the panel reads, so naming the crumb costs no extra
 * request.
 */
export function AdminStaffDetails() {
  const { staffId } = useParams<{ staffId: string }>();
  const parsedStaffId = Number(staffId);
  const isValidStaffId = Number.isFinite(parsedStaffId) && parsedStaffId > 0;

  const { staffDetails } = useStaffDetails(
    isValidStaffId ? parsedStaffId : undefined
  );
  const { staffUser } = useStaffUser(staffDetails?.userId);

  const staffName = staffUser
    ? `${staffUser.firstName} ${staffUser.lastName}`.trim()
    : "";
  // Falls back to the id until the account loads, so the crumb never blinks
  // through an empty string.
  const currentLabel =
    staffName || (isValidStaffId ? `Staff #${parsedStaffId}` : "Staff details");

  document.title = `${currentLabel} - Admin`;

  return (
    <PageContainer>
      <PageIntro>
        <AdminBreadcrumb
          items={[
            { label: "Staff", to: "/admin/staff" },
            { label: currentLabel },
          ]}
        />
        <PageHeader>
          <PageTitle>{currentLabel}</PageTitle>
        </PageHeader>
      </PageIntro>

      {isValidStaffId ? (
        <StaffDetailsPanel staffId={parsedStaffId} />
      ) : (
        <ErrorMessage>That staff id isn&apos;t valid.</ErrorMessage>
      )}
    </PageContainer>
  );
}
